import { definedNotNull } from '@df/common';
import { z } from 'zod';

const knownTransactionTypeNames = [
  'AccountTypeTransfer',
  'AccrualAccounting',
  'AdvancesFixedAssets',
  'BadDebtLoss',
  'Benefits',
  'BusinessAcquisitions',
  'CalculateDeferredTax',
  'CalculateTaxOnProfit',
  'CalculationSpecialEmployersContribution',
  'CashTransaction',
  'ChangeDeferredEmployersContribution',
  'ChangeExcessDepreciation',
  'ChangeOtherAppropriations',
  'ChangeProvision',
  'ChangeSalaryLiability',
  'ChangeTaxAllocationReserve',
  'CurrencyAdjustment',
  'CustomerPayment',
  'DevaluationFixedAssets',
  'DiscardFixedAssets',
  'DividendListedShare',
  'DividendUnlistedShare',
  'FixedAssetRepair',
  'GroupContribution',
  'ImpairmentListedShare',
  'ImpairmentReceivable',
  'ImpairmentUnlistedShare',
  'IncomesInterest',
  'IncomesOtherOperatingIncome',
  'IncomesSubsidy',
  'InputVatTransfer',
  'InterestLoan',
  'InterestPurchase',
  'IntergroupTransfer',
  'InternalAccounting',
  'LiabilityTransfer',
  'MovementInventory',
  'NewIssue',
  'NewLiability',
  'NewLoan',
  'NewReceivable',
  'NonInventoryPurchase',
  'NonInventoryPurchaseReverseCharge',
  'OutputVatTransfer',
  'OwnContribution',
  'OwnWithdrawal',
  'PaymentBenefits',
  'PaymentFora',
  'PaymentLiability',
  'PaymentPensionInsurance',
  'PaymentSalary',
  'PaymentTaxFreeCompensation',
  'PrepaidExpense',
  'PrepaidIncome',
  'ProviderPayment',
  'PurchaseConsumableAssets',
  'PurchaseDiscountInventory',
  'PurchaseFixedAssets',
  'PurchaseInventory',
  'PurchaseInventoryReverseCharge',
  'PurchaseListedShare',
  'PurchaseUnlistedShare',
  'ReceivableTransfer',
  'ReconciliationFora',
  'RepaymentLoan',
  'RepaymentReceivable',
  'RefundSubsidy',
  'ResultAdjustment',
  'ReversalDevaluationFixedAssets',
  'SalaryEmployersContribution',
  'SaleListedShare',
  'SaleUnlistedShare',
  'Sales',
  'SalesWithSettlement',
  'ShareholderDividend',
  'ShareholdersContribution',
  'SpecialTax',
  'TaxAccountPayment',
  'TaxEmployersFeePayment',
  'TaxInterestExpense',
  'TaxInterestIncome',
  'TaxPayment',
  'TaxVatPayment',
  'TaxWithholdingTaxAndEmployersFeePayment',
  'TaxWithholdingTaxPayment',
  'TaxableWithdrawals',
  'TransferEquity',
  'VatTransfer',
  'WithdrawalEndowmentInsurance',
] as const;

const knownTransactionTypeSchema = z.enum(knownTransactionTypeNames);
type KnownTransactionType = z.infer<typeof knownTransactionTypeSchema>;

// TODO#1730
const knownTransactionTypeWithUnknownNames = [...knownTransactionTypeNames, 'UnknownType'] as const;
const knownTransactionTypeWithUnknownSchema = z.enum(knownTransactionTypeWithUnknownNames);
type KnownTransactionTypeWithUnknown = z.infer<typeof knownTransactionTypeWithUnknownSchema>;

const knownTransactionTypeWithUnknownValues = new Set<string>(knownTransactionTypeWithUnknownNames);

/** Membership test for a raw string, without allocating a Zod parse result per call. */
function isKnownTransactionTypeWithUnknown(
  value: string,
): value is KnownTransactionTypeWithUnknown {
  return knownTransactionTypeWithUnknownValues.has(value);
}

const detailedTransactionTypeSchema = z.object({
  isReversed: z.boolean(),
  transactionType: knownTransactionTypeSchema,
});
type DetailedTransactionType = z.infer<typeof detailedTransactionTypeSchema>;

// TODO#1730
const detailedTransactionTypeWithUnknownSchema = detailedTransactionTypeSchema
  .or(z.literal('UnknownType'))
  .optional()
  .transform((transactionType) => {
    return definedNotNull(transactionType) ? transactionType : ('UnknownType' as const);
  });
type DetailedTransactionTypeWithUnknown = z.infer<typeof detailedTransactionTypeWithUnknownSchema>;

export {
  detailedTransactionTypeSchema,
  detailedTransactionTypeWithUnknownSchema,
  isKnownTransactionTypeWithUnknown,
  knownTransactionTypeSchema,
  knownTransactionTypeWithUnknownSchema,
  type DetailedTransactionType,
  type DetailedTransactionTypeWithUnknown,
  type KnownTransactionType,
  type KnownTransactionTypeWithUnknown,
};
